import { useTheme } from '@mui/material';
import type {
  ExternalProjectImportEntity,
  ExternalProjectImportError,
  ExternalProjectImportPreview,
} from '@provision/entity-types';
import { CircleAlert, FileText, FolderOpen, House, User } from 'lucide-react';
import { useState } from 'react';
import {
  ModalList,
  type ModalListItem,
  type ModalListLoadMore,
} from 'src/components/ModalContainer';
import { entityChangeToString, getRecordKey } from './import-results.utils';

const ENTITY_ICONS: Record<ExternalProjectImportEntity['entity'], typeof FileText> = {
  Project: FolderOpen,
  Site: House,
  Client: User,
};

interface ImportPreviewListProps {
  records: ExternalProjectImportPreview[];
  errors?: ExternalProjectImportError<string | number>[];
  /** Project names by id, for the projects the preview could not read. */
  projectNames: Map<string, string>;
  loading: boolean;
  loadMore?: ModalListLoadMore;
  /** Prefix of the filter and row test ids; a project's row is `-option-record-<id>`. */
  testId: string;
}

/** What an import would create and update: a folder per project with its sites, clients and projects. */
export const ImportPreviewList = ({
  records,
  errors,
  projectNames,
  loading,
  loadMore,
  testId,
}: ImportPreviewListProps) => {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const group = errors?.length ? 'Ready to import' : undefined;

  const errorRows: ModalListItem[] = (errors ?? []).map(({ projectId, error }) => ({
    id: `error-${projectId}`,
    title: projectNames.get(String(projectId)) ?? `Project ${projectId}`,
    description: error,
    icon: <CircleAlert color={theme.palette.error.main} />,
    group: 'Could not preview',
  }));

  const recordRows = records.flatMap((record, index): ModalListItem[] => {
    const key = getRecordKey(record, index);
    const expanded = !collapsed.has(key);
    const changes = [
      ...(record.created ?? []).map((entity) => ({ entity, change: 'created' as const })),
      ...(record.updated ?? []).map((entity) => ({ entity, change: 'updated' as const })),
    ];
    const folder: ModalListItem = {
      id: `record-${key}`,
      title: record.projectName,
      description: record.address || 'No address',
      icon: <FileText />,
      group,
      expanded,
    };
    if (!expanded) {
      return [folder];
    }
    return [
      folder,
      ...changes.map(({ entity, change }, changeIndex) => {
        const Icon = ENTITY_ICONS[entity.entity];
        return {
          id: `${key}-${change}-${changeIndex}`,
          title: entity.name,
          description: entityChangeToString(entity, change),
          icon: <Icon />,
          group,
          level: 1,
        };
      }),
    ];
  });

  const toggle = ({ id }: ModalListItem, expand: boolean) => {
    const key = id.replace(/^record-/, '');
    setCollapsed((current) => {
      const next = new Set(current);
      if (expand) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <ModalList
      items={[...errorRows, ...recordRows]}
      onExpandedChange={toggle}
      loading={loading}
      loadMore={loadMore}
      placeholder="Search"
      emptyText="Could not generate a preview for these projects."
      testId={testId}
    />
  );
};
